import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import AnnoSelector from "@/components/AnnoSelector";
import AlunniSendBar from "@/components/AlunniSendBar";
import IscrizioneBadge from "@/components/IscrizioneBadge";
import MovimentiCollegatiModal from "@/components/MovimentiCollegatiModal";
import useAlunniSelection from "@/lib/useAlunniSelection";

const fmtAnnoLabel = (a) => {
  const n = Number(a);
  if (!n) return "—";
  return `${n}/${String(n + 1).slice(-2)}`;
};

const STATO_COLORS = {
  frequentante: { bg: "#e8f5e9", color: "#1b5e20", border: "#a5d6a7" },
  dimesso: { bg: "#ffebee", color: "#c62828", border: "#ef9a9a" },
  iscritto_non_frequentante: { bg: "#fff3e0", color: "#e65100", border: "#ffcc80" },
  non_definito: { bg: "#eceff1", color: "#546e7a", border: "#cfd8dc" },
};

const CICLO_LABEL_FROM_KEY = {
  infanzia: "INFANZIA",
  uscito: "USCITO",
  primaria_1: "PRIMARIA 1", primaria_2: "PRIMARIA 2", primaria_3: "PRIMARIA 3",
  primaria_4: "PRIMARIA 4", primaria_5: "PRIMARIA 5",
  secondaria_1: "SECONDARIA 1", secondaria_2: "SECONDARIA 2", secondaria_3: "SECONDARIA 3",
};

/** Spunte compatti per le colonne anno scolastico.
 *  Richiesta utente (2026-02-16): selezione diretta tramite spunte mutuamente esclusive,
 *  modificabili solo nella colonna dell'anno scolastico di gestione.
 *  Labels MAIUSCOLE: I, P1-P5 (Primaria), M1-M3 (Medie / Secondaria 1° grado), U (Uscito). */
const CICLI_9 = [
  { key: "infanzia",     short: "I",  color: "#7b1fa2" },
  { key: "primaria_1",   short: "P1", color: "#2e7d32" },
  { key: "primaria_2",   short: "P2", color: "#2e7d32" },
  { key: "primaria_3",   short: "P3", color: "#2e7d32" },
  { key: "primaria_4",   short: "P4", color: "#2e7d32" },
  { key: "primaria_5",   short: "P5", color: "#2e7d32" },
  { key: "secondaria_1", short: "M1", color: "#1565c0" },
  { key: "secondaria_2", short: "M2", color: "#1565c0" },
  { key: "secondaria_3", short: "M3", color: "#1565c0" },
  { key: "uscito",       short: "U",  color: "#b71c1c" },
];

const CICLO_OPTIONS = [
  { value: "", label: "— (rimuovi override, usa lookup)" },
  { value: "infanzia", label: "INFANZIA" },
  { value: "primaria_1", label: "PRIMARIA 1" },
  { value: "primaria_2", label: "PRIMARIA 2" },
  { value: "primaria_3", label: "PRIMARIA 3" },
  { value: "primaria_4", label: "PRIMARIA 4" },
  { value: "primaria_5", label: "PRIMARIA 5" },
  { value: "secondaria_1", label: "SECONDARIA 1" },
  { value: "secondaria_2", label: "SECONDARIA 2" },
  { value: "secondaria_3", label: "SECONDARIA 3" },
  { value: "uscito", label: "USCITO" },
];

function nextCicloKey(ciclo) {
  if (!ciclo) return "infanzia";
  if (ciclo === "infanzia") return "primaria_1";
  if (ciclo.startsWith("primaria_")) return "secondaria_1";
  if (ciclo.startsWith("secondaria_")) return "uscito";
  return null;
}

export default function ScuolaCurriculumPage() {
  const { annoCorrente } = useAuth();
  const sel = useAlunniSelection();
  const [items, setItems] = useState([]);
  const [anni, setAnni] = useState([]);
  const [annoGestione, setAnnoGestione] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  // 🆕 Movimenti Iscrizione (Cau=1) per gli alunni visibili — batch counts
  const [iscrCounts, setIscrCounts] = useState({});
  const [iscrModal, setIscrModal] = useState(null);
  // 🆕 Pre-fill del filtro RICERCA da query string (?cerca=Rossi) usato dai deep-link (es. dal dettaglio alunno)
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cerca = params.get("cerca");
    if (cerca) setQ(cerca);
  }, [location.search]);
  const [filterCiclo, setFilterCiclo] = useState("");
  const [filterHasGoogle, setFilterHasGoogle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef(null);
  const fileNuoviRef = useRef(null);
  const [uploadingNuovi, setUploadingNuovi] = useState(false);
  const [nuoviResult, setNuoviResult] = useState(null);
  const [editModal, setEditModal] = useState(null); // {cod, cog, nom, anno, ciclo}
  // Modale "Modifiche recenti agli override"
  const [recentModal, setRecentModal] = useState(false);
  const [recentRows, setRecentRows] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentSelected, setRecentSelected] = useState({}); // { cod: true }

  const openRecent = async () => {
    setRecentModal(true);
    setRecentLoading(true);
    setRecentSelected({});
    try {
      const r = await api.get("/scuola-curriculum/recent-changes", { params: { anno: annoGestione || annoCorrente, limit: 100 } });
      setRecentRows(r.data?.items || []);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore caricamento modifiche recenti");
    } finally { setRecentLoading(false); }
  };

  const restoreRecent = async () => {
    const cods = Object.entries(recentSelected).filter(([, v]) => v).map(([k]) => parseInt(k));
    if (cods.length === 0) { alert("Seleziona almeno un alunno"); return; }
    if (!window.confirm(`Ripristinare il lookup automatico (cancellare l'override) per ${cods.length} alunni?\n\nGli alunni torneranno al ciclo derivato dalla loro classe corrente.`)) return;
    try {
      const r = await api.post("/scuola-curriculum/recent-changes/restore-lookup", { anno: annoGestione || annoCorrente, cods });
      alert(`Ripristinati ${r.data?.removed ?? cods.length} override`);
      await openRecent();
      await load(annoGestione);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore ripristino");
    }
  };

  const fmtUpdated = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return String(iso); }
  };

  const load = async (annoGest = annoGestione) => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (annoGest) params.anno_gestione = annoGest;
      const r = await api.get("/scuola-curriculum", { params });
      setItems(r.data?.items || []);
      setAnni(r.data?.anni || []);
      if (r.data?.anno_gestione && !annoGestione) {
        setAnnoGestione(r.data.anno_gestione);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  // Init / sync con annoCorrente globale (cambio dal selettore in alto sincronizza tutte le tab)
  useEffect(() => {
    if (!annoCorrente) return;
    setAnnoGestione(annoCorrente);
    load(annoCorrente);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annoCorrente]);

  const filtered = useMemo(() => {
    const qn = (q || "").trim().toLowerCase();
    return items.filter(it => {
      if (qn) {
        const blob = `${it.Cog} ${it.Nom} ${it.CodFis} ${it.google_email || ""}`.toLowerCase();
        if (!blob.includes(qn)) return false;
      }
      if (filterCiclo) {
        const c = it.ciclo_attuale || "";
        if (filterCiclo === "_NONE_") { if (c) return false; }
        else if (filterCiclo === "primaria") { if (!c.startsWith("primaria_")) return false; }
        else if (filterCiclo === "secondaria") { if (!c.startsWith("secondaria_")) return false; }
        else if (c !== filterCiclo) return false;
      }
      if (filterHasGoogle === "yes" && !it.google_email) return false;
      if (filterHasGoogle === "no" && it.google_email) return false;
      if (filterHasGoogle === "no_canossa") {
        const em = (it.google_email || "").toLowerCase();
        if (em.endsWith("@canossadiaz.it")) return false;
      }
      return true;
    });
  }, [items, q, filterCiclo, filterHasGoogle]);

  /** Raggruppamento per ciclo curriculum (NON più per settore/classe Segremat).
   * Ordine: SENZA SPUNTE · I · P1..P5 · M1..M3 · U
   */
  const grouped = useMemo(() => {
    const cicloToBucket = (ciclo) => {
      if (!ciclo) return { key: "_NONE_", label: "— SENZA SPUNTE", color: { bg: "#37474f", fg: "white" }, order: 999 };
      if (ciclo === "infanzia") return { key: "I", label: "I · INFANZIA", color: { bg: "#7b1fa2", fg: "white" }, order: 10 };
      if (ciclo === "uscito") return { key: "U", label: "U · USCITI", color: { bg: "#b71c1c", fg: "white" }, order: 900 };
      let m = String(ciclo).match(/^primaria_(\d+)$/);
      if (m) {
        const n = m[1];
        return { key: `P${n}`, label: `P${n} · PRIMARIA ${n}`, color: { bg: "#2e7d32", fg: "white" }, order: 100 + parseInt(n, 10) };
      }
      m = String(ciclo).match(/^secondaria_(\d+)$/);
      if (m) {
        const n = m[1];
        return { key: `M${n}`, label: `M${n} · SECONDARIA ${n}`, color: { bg: "#1565c0", fg: "white" }, order: 200 + parseInt(n, 10) };
      }
      return { key: "_NONE_", label: "— SENZA SPUNTE", color: { bg: "#37474f", fg: "white" }, order: 999 };
    };
    const groups = new Map();
    for (const it of filtered) {
      const bucket = cicloToBucket(it.ciclo_attuale || "");
      if (!groups.has(bucket.key)) {
        groups.set(bucket.key, {
          key: bucket.key,
          isUscito: bucket.key === "U",
          settore: "",
          classe: "",
          label: bucket.label,
          color: bucket.color,
          order: bucket.order,
          items: [],
        });
      }
      groups.get(bucket.key).items.push(it);
    }
    const arr = [...groups.values()];
    arr.sort((a, b) => a.order - b.order);
    // Sort interno: per cognome poi nome
    for (const g of arr) {
      g.items.sort((x, y) => {
        const cx = String(x.Cog || "").toLowerCase();
        const cy = String(y.Cog || "").toLowerCase();
        if (cx !== cy) return cx.localeCompare(cy);
        return String(x.Nom || "").toLowerCase().localeCompare(String(y.Nom || "").toLowerCase());
      });
    }
    return arr;
  }, [filtered, annoGestione]);

  // 🆕 Batch fetch dei conteggi movimenti iscrizione (Cau=1) per anno gestione
  useEffect(() => {
    const cods = (filtered || []).map((it) => Number(it.Cod)).filter(Boolean);
    if (!cods.length || !annoGestione) { setIscrCounts({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/alunni/iscrizione-movimenti/counts", {
          cods, anno: Number(annoGestione),
        });
        if (!cancelled) setIscrCounts(r.data?.counts || {});
      } catch (e) {
        console.warn("[curriculum] iscr-counts fetch failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [filtered, annoGestione]);

  // 🆕 Apre modal movimenti iscrizione per un alunno
  const openIscrizioneModal = async (cod) => {
    try {
      const r = await api.get(`/alunni/${cod}/iscrizione-movimenti`, { params: { anno: Number(annoGestione) } });
      const resp = r.data || {};
      setIscrModal({
        iscr: { alunno_cod: resp.alunno_cod, cognome: resp.cognome, nome: resp.nome, anno: resp.anno },
        data: resp,
      });
    } catch (e) {
      alert("Errore caricamento movimenti iscrizione: " + (e.response?.data?.detail || e.message));
    }
  };

  const fileEmailArchRef = useRef(null);
  const [uploadingEmailArch, setUploadingEmailArch] = useState(false);
  const [emailArchResult, setEmailArchResult] = useState(null);

  const onUploadEmailArchive = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingEmailArch(true); setEmailArchResult(null); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/scuola-curriculum/import-emails-archive", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEmailArchResult(r.data);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Errore durante l'importazione archivio email");
    } finally {
      setUploadingEmailArch(false);
      if (fileEmailArchRef.current) fileEmailArchRef.current.value = "";
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadResult(null); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/scuola-curriculum/import-google-email", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(r.data);
      await load();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Errore durante l'importazione");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onUploadNuoviAlunni = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const annoTarget = (annoGestione || annoCorrente) + 1;
    const ok = window.confirm(
      `Importa nuovi alunni dal file ${file.name}?\n\n` +
      `Verranno creati nuovi record in anagrafica per l'A.S. ${annoTarget}/${annoTarget + 1} ` +
      `con ciclo iniziale INFANZIA (settore A).\n\n` +
      `Premi OK per visualizzare prima l'anteprima (dry-run).`
    );
    if (!ok) return;
    setUploadingNuovi(true); setNuoviResult(null); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("anno", String(annoTarget));
      fd.append("ciclo", "infanzia");
      fd.append("dry_run", "true");
      const r1 = await api.post("/scuola-curriculum/import-nuovi-alunni", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const preview = r1.data;
      const sumLines = (preview.created || []).slice(0, 8).map(c => `· ${c.Cog} ${c.Nom} (${c.CodFis})`).join("\n");
      const more = (preview.inserted || 0) > 8 ? `\n… e altri ${preview.inserted - 8} alunni` : "";
      const confirm2 = window.confirm(
        `ANTEPRIMA importazione (A.S. ${annoTarget}/${annoTarget + 1}, settore A INFANZIA):\n\n` +
        `📥 Nuovi alunni da inserire: ${preview.inserted}\n` +
        `⏭ Già presenti (skip): ${preview.skipped}\n\n` +
        `Esempio:\n${sumLines}${more}\n\nConfermare l'inserimento definitivo?`
      );
      if (!confirm2) {
        setNuoviResult({ ...preview, _aborted: true });
        return;
      }
      // Inserimento reale
      const fd2 = new FormData();
      fd2.append("file", file);
      fd2.append("anno", String(annoTarget));
      fd2.append("ciclo", "infanzia");
      fd2.append("dry_run", "false");
      const r2 = await api.post("/scuola-curriculum/import-nuovi-alunni", fd2, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNuoviResult(r2.data);
      await load();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Errore durante l'importazione nuovi alunni");
    } finally {
      setUploadingNuovi(false);
      if (fileNuoviRef.current) fileNuoviRef.current.value = "";
    }
  };

  const doAvanza = async (it) => {
    const curLabel = it.ciclo_attuale_label || "—";
    const nextKey = nextCicloKey(it.ciclo_attuale);
    if (!nextKey) {
      alert(`Impossibile avanzare ulteriormente "${curLabel}".`);
      return;
    }
    const nextLabel = CICLO_LABEL_FROM_KEY[nextKey] || nextKey;
    const ok = window.confirm(
      `Avanzamento ciclo per ${it.Cog} ${it.Nom} (A.S. ${fmtAnnoLabel(annoGestione)})\n\n` +
      `${curLabel || "(nessuno)"}  →  ${nextLabel}\n\nConfermi?`
    );
    if (!ok) return;
    try {
      await api.post(`/scuola-curriculum/${it.Cod}/avanza`, { anno: annoGestione });
      await load();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const doSetCiclo = async (cod, anno, ciclo) => {
    // anno opzionale (default: annoGestione, per retro-compatibilità con SetCicloModal)
    const annoEff = anno || annoGestione;
    try {
      await api.put(`/scuola-curriculum/${cod}/ciclo`, { anno: annoEff, ciclo });
      setEditModal(null);
      await load();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const doSetSnap = async (cod, anno, settore, classe) => {
    try {
      await api.put(`/scuola-curriculum/${cod}/snap`, { anno: anno || annoGestione, settore, classe });
      await load();
    } catch (e) {
      alert("Errore salvataggio settore/classe: " + (e.response?.data?.detail || e.message));
    }
  };

  const doPromuoviClasse = async (group) => {
    const annoSrc = Number(annoGestione);
    const annoDst = annoSrc + 1;
    const set = group.settore;
    const cla = group.classe;
    const n = group.items.length;
    const msg = `Promuovere TUTTI i ${n} alunni della classe ${set} ${cla} dall'anno ${annoSrc}/${annoSrc + 1} all'anno ${annoDst}/${annoDst + 1}?\n\n` +
      `Per ciascun alunno, il ciclo nell'anno ${annoDst}/${annoDst + 1} verrà impostato al successivo (es. PRIMARIA 1 → PRIMARIA 2, PRIMARIA 5 → SECONDARIA 1, SECONDARIA 3 → USCITO).\n\n` +
      `Vuoi vedere prima un'anteprima?`;
    if (!window.confirm(msg)) return;
    try {
      // 1) dry-run per preview
      const r1 = await api.post("/scuola-curriculum/promuovi-classe", {
        anno_sorgente: annoSrc, anno_destinazione: annoDst,
        settore: set, classe: cla, dry_run: true,
      });
      const preview = r1.data?.items || [];
      if (preview.length === 0) {
        alert(`⚠ ${r1.data?.warning || `Nessun alunno trovato per ${set} ${cla} anno ${annoSrc}`}\n\nVerifica che il backend sia aggiornato e che ci siano alunni in DB per quella classe nell'anno sorgente.`);
        return;
      }
      const summary = preview.slice(0, 10).map(it =>
        `· ${it.cog} ${it.nom}: ${it.ciclo_src_label || "—"} → ${it.ciclo_new_label || "—"}`
      ).join("\n");
      const more = preview.length > 10 ? `\n... e altri ${preview.length - 10} alunni` : "";
      if (!window.confirm(`ANTEPRIMA promozione ${set} ${cla} (${preview.length} alunni):\n\n${summary}${more}\n\nConfermare la promozione?`)) return;
      // 2) esegui
      const r2 = await api.post("/scuola-curriculum/promuovi-classe", {
        anno_sorgente: annoSrc, anno_destinazione: annoDst,
        settore: set, classe: cla, dry_run: false,
      });
      alert(`✓ Promozione completata: ${r2.data?.updated || 0} alunni aggiornati nell'A.S. ${annoDst}/${annoDst + 1}.`);
      await load();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  /** Aggiorna inline la google_email di un alunno (su tutti i suoi anni). */
  const doUpdateEmail = async (cod, email) => {
    try {
      await api.put(`/scuola-curriculum/${cod}/google-email`, { google_email: email });
      // Aggiorna lo stato locale senza ricaricare tutto
      setItems(prev => prev.map(it => it.Cod === cod ? { ...it, google_email: email || "" } : it));
    } catch (e) {
      throw e;
    }
  };

  // Anni visibili in tabella: anno precedente + anno gestione + successivo (tutti modificabili).
  const visibleAnni = annoGestione ? [Number(annoGestione) - 1, Number(annoGestione), Number(annoGestione) + 1] : anni;

  return (
    <div data-testid="scuola-curriculum-page" style={{ padding: "12px 20px" }}>
      <h1 style={{ fontSize: 18, color: "#4a148c", marginBottom: 10 }}>🎓 Curriculum alunni</h1>

      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 10, padding: 12, background: "#fafafa", border: "1px solid #e0e0e0", flexWrap: "wrap" }}>
        <AnnoSelector testId="curr-anno-attivo" />
        <Field label="RICERCA">
          <input data-testid="curr-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Cognome, nome, CF, email…"
            style={{ width: 260, padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }} />
        </Field>
        <Field label={`CICLO (${annoGestione ? fmtAnnoLabel(annoGestione) : "—"})`}>
          {(() => {
            const buckets = [
              { v: "", lbl: "TUTTI", bg: "#455a64" },
              { v: "_NONE_", lbl: "?", bg: "#37474f", title: "Senza spunte" },
              { v: "infanzia", lbl: "I", bg: "#7b1fa2", title: "Infanzia" },
              { v: "primaria_1", lbl: "P1", bg: "#2e7d32" },
              { v: "primaria_2", lbl: "P2", bg: "#2e7d32" },
              { v: "primaria_3", lbl: "P3", bg: "#2e7d32" },
              { v: "primaria_4", lbl: "P4", bg: "#2e7d32" },
              { v: "primaria_5", lbl: "P5", bg: "#2e7d32" },
              { v: "secondaria_1", lbl: "M1", bg: "#1565c0" },
              { v: "secondaria_2", lbl: "M2", bg: "#1565c0" },
              { v: "secondaria_3", lbl: "M3", bg: "#1565c0" },
              { v: "uscito", lbl: "U", bg: "#b71c1c", title: "Usciti" },
            ];
            return (
              <div data-testid="curr-filter-ciclo-toolbar" style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                {buckets.map(b => {
                  const active = filterCiclo === b.v;
                  return (
                    <button key={b.v || "all"} type="button"
                      data-testid={`curr-filter-ciclo-${b.v || "all"}`}
                      onClick={() => setFilterCiclo(b.v)}
                      title={b.title || b.lbl}
                      style={{
                        padding: "5px 10px", minWidth: 32,
                        fontSize: 11, fontWeight: 800, letterSpacing: 0.3,
                        background: active ? b.bg : "white",
                        color: active ? "white" : b.bg,
                        border: `2px solid ${b.bg}`,
                        cursor: "pointer", borderRadius: 3,
                      }}>{b.lbl}</button>
                  );
                })}
              </div>
            );
          })()}
        </Field>
        <Field label="GOOGLE EMAIL">
          <select data-testid="curr-has-google" value={filterHasGoogle} onChange={e => setFilterHasGoogle(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 12, minWidth: 220 }}>
            <option value="">Tutti</option>
            <option value="yes">Solo con email</option>
            <option value="no">Solo senza email</option>
            <option value="no_canossa">🚨 Senza canossadiaz.it</option>
          </select>
        </Field>
        <button data-testid="curr-apply" onClick={() => load(annoGestione)}
          style={{ padding: "6px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          🔄 Aggiorna
        </button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button data-testid="curr-recent-btn" onClick={openRecent}
            title="Mostra gli ultimi override modificati per quest'anno (utile per recuperare modifiche errate)"
            style={{ padding: "6px 14px", background: "#6a1b9a", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            🕒 Modifiche recenti
          </button>
          <input ref={fileNuoviRef} type="file" accept=".csv" onChange={onUploadNuoviAlunni} style={{ display: "none" }} data-testid="curr-import-nuovi-input" />
          <button data-testid="curr-import-nuovi-btn" onClick={() => fileNuoviRef.current?.click()} disabled={uploadingNuovi}
            title={`Importa nuovi alunni iscritti per l'A.S. ${(annoGestione || annoCorrente) + 1}/${String((annoGestione || annoCorrente) + 2).slice(-2)} (settore A INFANZIA)`}
            style={{ padding: "6px 14px", background: "#e65100", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: uploadingNuovi ? "wait" : "pointer" }}>
            {uploadingNuovi ? "⏳ Importazione..." : `➕ Importa Nuovi Alunni (${(annoGestione || annoCorrente) + 1}/${String((annoGestione || annoCorrente) + 2).slice(-2)})`}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx" onChange={onUpload} style={{ display: "none" }} data-testid="curr-import-input" />
          <button data-testid="curr-import-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
            style={{ padding: "6px 14px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: uploading ? "wait" : "pointer" }}>
            📥 Importa Google Email (Excel)
          </button>
          <input ref={fileEmailArchRef} type="file" accept=".xls,.xlsx" onChange={onUploadEmailArchive} style={{ display: "none" }} data-testid="curr-import-emailarch-input" />
          <button data-testid="curr-import-emailarch-btn" onClick={() => fileEmailArchRef.current?.click()} disabled={uploadingEmailArch}
            title="Importa archivio email genitori (Cognome | Nome | CodFis | Email 1 | Email 2). Le email già collegate vengono saltate."
            style={{ padding: "6px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: uploadingEmailArch ? "wait" : "pointer" }}>
            {uploadingEmailArch ? "⏳ Importazione..." : "✉ Importa Archivio Email (xls/xlsx)"}
          </button>
        </div>
      </div>

      {emailArchResult && (
        <div data-testid="curr-import-emailarch-result"
          style={{ padding: 12, background: "#e3f2fd", border: "1px solid #64b5f6", borderRadius: 4, marginBottom: 10, fontSize: 12, color: "#0d47a1" }}>
          ✉ Archivio email <b>{emailArchResult.file}</b>: <b>{emailArchResult.inserted}</b> nuove email collegate ·{" "}
          <b>{emailArchResult.skipped_already_present}</b> già presenti (saltate) ·{" "}
          <b>{emailArchResult.skipped_invalid}</b> non valide ·{" "}
          alunni coinvolti: <b>{emailArchResult.alunni_matched}</b>
          {emailArchResult.not_found_count > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer", fontSize: 11, color: "#c62828" }}>⚠ {emailArchResult.not_found_count} alunni non trovati in anagrafica</summary>
              <div style={{ marginTop: 4, fontSize: 11, maxHeight: 140, overflow: "auto" }}>
                {(emailArchResult.not_found_sample || []).map((s, i) => <div key={i}>· {s}</div>)}
              </div>
            </details>
          )}
        </div>
      )}

      {nuoviResult && (
        <div data-testid="curr-import-nuovi-result"
          style={{ padding: 12, background: nuoviResult._aborted ? "#fff3e0" : "#e8f5e9", border: `1px solid ${nuoviResult._aborted ? "#ef6c00" : "#66bb6a"}`, borderRadius: 4, marginBottom: 10, fontSize: 12, color: nuoviResult._aborted ? "#e65100" : "#1b5e20" }}>
          {nuoviResult._aborted ? (
            <>⚠ Importazione annullata dall&apos;utente. <b>{nuoviResult.inserted}</b> alunni sarebbero stati inseriti, <b>{nuoviResult.skipped}</b> saltati.</>
          ) : (
            <>✅ Nuovi alunni importati: <b>{nuoviResult.inserted}</b> creati nell&apos;A.S. <b>{nuoviResult.anno}/{String(nuoviResult.anno + 1).slice(-2)}</b> (settore INFANZIA) · <b>{nuoviResult.skipped}</b> saltati (già presenti).</>
          )}
          {(nuoviResult.skipped_details || []).length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer", fontSize: 11, color: "#555" }}>Mostra alunni saltati ({nuoviResult.skipped_details.length})</summary>
              <div style={{ marginTop: 4, fontSize: 11, maxHeight: 120, overflow: "auto" }}>
                {nuoviResult.skipped_details.map((s, i) => (
                  <div key={i}>· {s.cog} {s.nom} — {s.reason}</div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {uploadResult && (
        <div data-testid="curr-import-result"
          style={{ padding: 12, background: "#e3f2fd", border: "1px solid #64b5f6", borderRadius: 4, marginBottom: 10, fontSize: 12, color: "#0d47a1" }}>
          ✅ Importazione completata (formato <b>{uploadResult.format || "?"}</b>):{" "}
          <b>{uploadResult.alunni_updated ?? "?"}</b> alunni aggiornati ·{" "}
          <b>{uploadResult.updated}</b> record totali ·{" "}
          <b>{uploadResult.skipped}</b> righe saltate ·{" "}
          <b>{uploadResult.not_found_count}</b> non trovati
          {uploadResult.not_found?.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer", fontSize: 11, color: "#555" }}>
                Mostra non trovati ({uploadResult.not_found_count})
              </summary>
              <div style={{ marginTop: 4, fontSize: 11, color: "#555", maxHeight: 120, overflow: "auto" }}>
                {uploadResult.not_found.join(" · ")}
                {uploadResult.not_found_count > uploadResult.not_found.length && ` …(+${uploadResult.not_found_count - uploadResult.not_found.length})`}
              </div>
            </details>
          )}
        </div>
      )}

      {err && <div style={{ padding: 8, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", marginBottom: 8, fontSize: 12, whiteSpace: "pre-wrap" }}>{err}</div>}
      {loading && <div style={{ padding: 12, fontSize: 12, color: "#666" }}>Caricamento…</div>}

      {/* Anni visualizzati: SOLO l'anno di gestione + il successivo (richiesta utente) */}

      <AlunniSendBar selected={sel.selected} onClear={sel.clear}
        label={`Curriculum ${annoGestione ? fmtAnnoLabel(annoGestione) : ""}`}
        anno={annoGestione} />

      <div style={{ overflowX: "auto", border: "1px solid #ddd" }}>
        <table data-testid="curr-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1100 }}>
          <thead>
            <tr style={{ background: "#1a237e", color: "white" }}>
              <th style={{ ...th, width: 30, textAlign: "center" }}>
                <input type="checkbox" data-testid="curr-selall"
                  title="Seleziona/Deseleziona tutti i visibili"
                  checked={filtered.length > 0 && filtered.every(it => sel.isSelected(it.Cod))}
                  onChange={(e) => e.target.checked ? sel.selectAll(filtered.map(it => it.Cod)) : sel.deselectAll(filtered.map(it => it.Cod))} />
              </th>
              <th style={th}>COD</th>
              <th style={th}>COGNOME</th>
              <th style={th}>NOME</th>
              <th style={{ ...th, width: 60, textAlign: "center" }} title="Settore corrente (dall'anagrafica alunni)">SET</th>
              <th style={{ ...th, width: 80, textAlign: "center" }} title="Classe attuale (dall'anagrafica alunni)">CLASSE</th>
              {visibleAnni.map(a => {
                const isGest = Number(a) === Number(annoGestione);
                const isPrev = Number(a) === Number(annoGestione) - 1;
                const isNext = Number(a) === Number(annoGestione) + 1;
                return (
                  <th key={a}
                    style={{
                      ...th, textAlign: "center", minWidth: 130,
                      background: isGest ? "#0d47a1" : (isPrev ? "#283593" : "#1a237e"),
                      borderBottom: isGest ? "3px solid #fbc02d" : th.borderBottom,
                      borderLeft: isGest ? "2px solid #fbc02d" : undefined,
                      borderRight: isGest ? "2px solid #fbc02d" : undefined,
                    }}
                    title={
                      isGest
                        ? `Anno di gestione ATTIVO (modificabile) — A.S. ${fmtAnnoLabel(a)}`
                        : (isPrev
                          ? `A.S. ${fmtAnnoLabel(a)} (anno precedente — modificabile)`
                          : `A.S. ${fmtAnnoLabel(a)} (anno successivo — modificabile)`)
                    }>
                    {fmtAnnoLabel(a)}
                    {isGest && (
                      <span style={{ display: "block", fontSize: 9, fontWeight: 700, color: "#fbc02d", marginTop: 2, letterSpacing: 0.5 }}>
                        ✏ COLONNA ATTIVA
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={4 + visibleAnni.length} style={{ padding: 20, textAlign: "center", color: "#999" }}>Nessun alunno</td></tr>
            )}
            {grouped.map((g, gIdx) => {
              const withMail = g.items.filter(it => it.google_email).length;
              return (
                <GroupBlock key={g.key}
                  group={g}
                  gIdx={gIdx}
                  colSpan={6 + visibleAnni.length}
                  withMail={withMail}
                  anni={visibleAnni}
                  annoGestione={annoGestione}
                  setEditModal={setEditModal}
                  doAvanza={doAvanza}
                  doSetCiclo={doSetCiclo}
                  doSetSnap={doSetSnap}
                  onEmailUpdate={doUpdateEmail}
                  onPromuoviClasse={doPromuoviClasse}
                  sel={sel}
                  iscrCounts={iscrCounts}
                  onOpenIscr={openIscrizioneModal}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, padding: 10, background: "#e8eaf6", border: "1px solid #9fa8da", borderRadius: 4, fontSize: 11, color: "#1a237e" }}>
        <b>ℹ Come funziona:</b> nella colonna <b>anno di gestione</b> (evidenziata in giallo) clicca direttamente la spunta del ciclo desiderato:
        <span style={{ display: "inline-block", marginLeft: 8 }}>
          <b>I</b> Infanzia · <b>P1-P5</b> Primaria · <b>M1-M3</b> Medie · <b>U</b> Uscito
        </span>
        <br />
        La selezione è esclusiva: il click su un'altra spunta sostituisce la precedente.
      </div>

      <div style={{ marginTop: 8, padding: 10, background: "#f3e5f5", border: "1px solid #ce93d8", borderRadius: 4, fontSize: 11, color: "#4a148c" }}>
        <b>📥 Import Google Email — formati supportati</b>
        <div style={{ display: "flex", gap: 16, marginTop: 6, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 280px", padding: 8, background: "white", border: "1px dashed #ce93d8" }}>
            <b>Formato A</b> — legacy (CF):<br/>
            <code style={{ fontSize: 10 }}>Codice Fiscale | Email Google</code>
          </div>
          <div style={{ flex: "1 1 280px", padding: 8, background: "white", border: "1px solid #4a148c" }}>
            <b>Formato B</b> — export Google Workspace:<br/>
            <code style={{ fontSize: 10 }}>First Name | Last Name | Email Address</code><br/>
            <span style={{ fontSize: 10, color: "#666" }}>(match per Cognome + Nome normalizzati)</span>
          </div>
        </div>
        <div style={{ marginTop: 6, fontSize: 10 }}>
          Il sistema rileva il formato dall'intestazione. L'email viene memorizzata su tutti gli anni dell'alunno.
          Al login con Google, se la mail corrisponde, l'alunno viene <b>linkato automaticamente</b> senza richiesta del CF.
        </div>
      </div>

      {/* Modal modifica esplicita */}
      {editModal && (
        <SetCicloModal
          data={editModal}
          onClose={() => setEditModal(null)}
          onSave={(ciclo) => doSetCiclo(editModal.cod, editModal.anno, ciclo)}
        />
      )}

      {/* Modal movimenti Iscrizione (Cau=1) */}
      {iscrModal && <MovimentiCollegatiModal data={iscrModal} onClose={() => setIscrModal(null)} />}

      {/* === Modal Modifiche Recenti agli Override === */}
      {recentModal && (
        <div data-testid="curr-recent-modal" onClick={() => setRecentModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: 6, maxWidth: 1100, width: "100%", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 24px rgba(0,0,0,0.3)" }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f3e5f5" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#4a148c" }}>🕒 Modifiche recenti — Override Ciclo</div>
                <div style={{ fontSize: 11, color: "#6a1b9a", marginTop: 2 }}>
                  Anno {fmtAnnoLabel(annoGestione || annoCorrente)} · ultime {recentRows.length} righe (più recenti in cima).
                  Seleziona e ripristina per cancellare l'override (l'alunno torna al ciclo derivato dalla classe corrente).
                </div>
              </div>
              <button onClick={() => setRecentModal(false)} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#6a1b9a" }}>×</button>
            </div>

            <div style={{ padding: "10px 18px", borderBottom: "1px solid #e0e0e0", display: "flex", gap: 10, alignItems: "center", background: "#fafafa", flexWrap: "wrap" }}>
              <button data-testid="curr-recent-select-diff" onClick={() => {
                  const next = {};
                  recentRows.forEach(r => { if (r.diff) next[r.cod] = true; });
                  setRecentSelected(next);
                }}
                style={{ padding: "5px 12px", background: "#ffa000", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                ⚠ Seleziona solo "diverso dal lookup"
              </button>
              <button data-testid="curr-recent-select-all" onClick={() => {
                  const next = {};
                  recentRows.forEach(r => { next[r.cod] = true; });
                  setRecentSelected(next);
                }}
                style={{ padding: "5px 12px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                ☑ Seleziona tutti
              </button>
              <button onClick={() => setRecentSelected({})}
                style={{ padding: "5px 12px", background: "#757575", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                Pulisci
              </button>
              <div style={{ marginLeft: "auto", fontSize: 12, color: "#555" }}>
                Selezionati: <b>{Object.values(recentSelected).filter(Boolean).length}</b> / {recentRows.length}
              </div>
              <button data-testid="curr-recent-restore" onClick={restoreRecent}
                disabled={Object.values(recentSelected).filter(Boolean).length === 0}
                style={{ padding: "6px 14px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3, opacity: Object.values(recentSelected).filter(Boolean).length === 0 ? 0.5 : 1 }}>
                ↺ Ripristina lookup automatico
              </button>
            </div>

            <div style={{ overflowY: "auto", flex: 1, padding: "0 18px" }}>
              {recentLoading ? (
                <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento…</div>
              ) : recentRows.length === 0 ? (
                <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessun override per questo anno.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead style={{ position: "sticky", top: 0, background: "#fafafa", zIndex: 1 }}>
                    <tr>
                      <th style={{ ...th, borderBottom: "2px solid #6a1b9a", width: 30 }}>☑</th>
                      <th style={{ ...th, borderBottom: "2px solid #6a1b9a" }}>Cod</th>
                      <th style={{ ...th, borderBottom: "2px solid #6a1b9a" }}>Cognome Nome</th>
                      <th style={{ ...th, borderBottom: "2px solid #6a1b9a" }}>Classe attuale</th>
                      <th style={{ ...th, borderBottom: "2px solid #6a1b9a" }}>Ciclo OVERRIDE</th>
                      <th style={{ ...th, borderBottom: "2px solid #6a1b9a" }}>Ciclo da LOOKUP</th>
                      <th style={{ ...th, borderBottom: "2px solid #6a1b9a" }}>Modificato il</th>
                      <th style={{ ...th, borderBottom: "2px solid #6a1b9a" }}>Da</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRows.map(r => (
                      <tr key={r.cod} data-testid={`curr-recent-row-${r.cod}`}
                        style={{ borderBottom: "1px solid #eee", background: r.diff ? "#fff3e0" : "white" }}>
                        <td style={{ ...td, textAlign: "center" }}>
                          <input data-testid={`curr-recent-chk-${r.cod}`} type="checkbox"
                            checked={!!recentSelected[r.cod]}
                            onChange={e => setRecentSelected(s => ({ ...s, [r.cod]: e.target.checked }))} />
                        </td>
                        <td style={{ ...td, fontFamily: "Consolas, monospace", fontWeight: 700 }}>{r.cod}</td>
                        <td style={td}>{r.Cog} {r.Nom}</td>
                        <td style={{ ...td, color: "#555" }}>{(r.SetNom || "—")} {(r.ClaNom || "")}</td>
                        <td style={{ ...td, fontWeight: 700, color: r.diff ? "#c62828" : "#1b5e20" }}>{r.ciclo_label || "—"}</td>
                        <td style={{ ...td, color: "#1565c0", fontWeight: 700 }}>{r.ciclo_lookup_label || "—"}</td>
                        <td style={{ ...td, fontSize: 11, color: "#555", whiteSpace: "nowrap" }}>{fmtUpdated(r.updated_at)}</td>
                        <td style={{ ...td, fontSize: 11, color: "#555" }}>{r.updated_by || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ padding: "10px 18px", borderTop: "1px solid #e0e0e0", background: "#fafafa", fontSize: 11, color: "#666" }}>
              <b>Legenda:</b> righe in <span style={{ background: "#fff3e0", padding: "1px 6px" }}>arancio</span> = l'override è diverso dal ciclo derivabile dalla classe attuale (probabile modifica intenzionale o errore).
              Le righe in bianco indicano coincidenza override = lookup (non urgenti).
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CicloCheckboxes({ current, editable, onSet, nasDat, dataTestId }) {
  // Estrae le 2 cifre finali dell'anno di nascita (es. "01/03/2023" → "23").
  // Accetta formati: dd/mm/yyyy, yyyy-mm-dd, dd-mm-yyyy.
  const annoNascita2 = (() => {
    const s = String(nasDat || "").trim();
    if (!s) return "";
    let m = s.match(/(\d{4})$/);                           // ...yyyy
    if (m) return m[1].slice(-2);
    m = s.match(/^(\d{4})-\d{2}-\d{2}/);                   // yyyy-mm-dd
    if (m) return m[1].slice(-2);
    return "";
  })();
  // Mappa colore ciclo → versione "blanda" (background per bottoni non selezionati)
  const SOFT_BG = {
    "#7b1fa2": "#f3e5f5",  // viola → viola chiarissimo
    "#2e7d32": "#e8f5e9",  // verde → verde chiarissimo
    "#1565c0": "#e3f2fd",  // blu → blu chiarissimo
    "#b71c1c": "#ffebee",  // rosso → rosso chiarissimo
  };
  // Dimensione bottone quadrato (height = width) — richiesta utente
  const SIDE = 36;
  return (
    <div style={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "center", width: "100%", minHeight: SIDE }}>
      {CICLI_9.map(c => {
        const sel = current === c.key;
        const softBg = SOFT_BG[c.color] || "#f5f5f5";
        return (
          <button
            key={c.key}
            type="button"
            title={`${CICLO_LABEL_FROM_KEY[c.key]}${editable ? "" : " (sola lettura)"}`}
            data-testid={`${dataTestId}-${c.short}`}
            disabled={!editable}
            onClick={() => { if (editable && !sel) onSet(c.key); }}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: c.short === "I" && annoNascita2 ? 46 : SIDE, height: SIDE, padding: 0, flex: "0 0 auto",
              fontSize: c.short === "I" && annoNascita2 ? 12 : 14, fontWeight: 900, letterSpacing: 0.4,
              border: `1px solid ${sel ? c.color : "#90a4ae"}`,
              background: sel ? c.color : softBg,
              color: sel ? "white" : c.color,
              cursor: editable ? "pointer" : "default",
              opacity: editable ? 1 : (sel ? 0.9 : 0.75),
              userSelect: "none",
              borderRadius: 0,
              boxShadow: sel ? "0 1px 3px rgba(0,0,0,0.25)" : "none",
              textShadow: sel ? "0 1px 1px rgba(0,0,0,0.25)" : "none",
            }}>
            {c.short === "I" && annoNascita2 ? `${c.short}${annoNascita2}` : c.short}
          </button>
        );
      })}
    </div>
  );
}

function GroupBlock({ group, gIdx, colSpan, withMail, anni, annoGestione, setEditModal, doAvanza, doSetCiclo, doSetSnap, onEmailUpdate, onPromuoviClasse, sel, iscrCounts, onOpenIscr }) {
  const [collapsed, setCollapsed] = useState(false);
  const n = group.items.length;
  const canPromote = !group.isUscito && group.settore && group.classe && annoGestione;
  return (
    <>
      <tr data-testid={`curr-group-${gIdx}`}
        onClick={() => setCollapsed(c => !c)}
        style={{ cursor: "pointer", background: group.color.bg, color: group.color.fg }}>
        <td colSpan={colSpan} style={{ padding: "8px 12px", fontWeight: 800, letterSpacing: 0.5, fontSize: 12 }}>
          <span style={{ display: "inline-block", width: 16 }}>{collapsed ? "▶" : "▼"}</span>
          <span style={{ marginLeft: 4 }}>{group.label}</span>
          <span style={{ marginLeft: 14, fontSize: 11, opacity: 0.95, fontWeight: 600 }}>
            · <b>{n}</b> alunn{n === 1 ? "o" : "i"}
            {withMail > 0 && <> · 📧 {withMail} con mail Google</>}
          </span>
          {canPromote && (
            <button
              data-testid={`curr-promote-${gIdx}`}
              onClick={(e) => { e.stopPropagation(); onPromuoviClasse(group); }}
              title={`Promuovi tutti gli alunni della classe ${group.settore} ${group.classe} all'anno successivo (${annoGestione + 1}/${annoGestione + 2})`}
              style={{
                marginLeft: 14, padding: "3px 10px",
                background: "rgba(255,255,255,0.18)", color: "white",
                border: "1px solid rgba(255,255,255,0.5)", borderRadius: 3,
                fontSize: 10, fontWeight: 700, letterSpacing: 0.3, cursor: "pointer",
              }}>
              🎓 Promuovi classe → {annoGestione + 1}/{(annoGestione + 2).toString().slice(-2)}
            </button>
          )}
        </td>
      </tr>
      {!collapsed && group.items.map((it, i) => {
        const rowIdx = `${gIdx}-${i}`;
        const isSel = sel ? sel.isSelected(it.Cod) : false;
        return (
          <tr key={it.Cod} data-testid={`curr-row-${rowIdx}`}
            style={{ borderBottom: "1px solid #eee", background: isSel ? "#f3e5f5" : (i % 2 ? "#fafafa" : "white") }}>
            <td style={{ ...td, textAlign: "center" }}>
              {sel && (
                <input type="checkbox" data-testid={`curr-sel-${it.Cod}`}
                  checked={isSel} onChange={() => sel.toggle(it.Cod)} />
              )}
            </td>
            <td style={{ ...td, fontFamily: "Consolas, monospace", color: "#888" }}>{it.Cod}</td>
            <td style={{ ...td, fontWeight: 700 }}>{it.Cog || "—"}</td>
            <td style={td}>{it.Nom || "—"}</td>
            <td style={{ ...td, textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, color: "#37474f" }}>{it.SetNom || "—"}</td>
            <td style={{ ...td, textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1" }}>{it.ClaNom || "—"}</td>
            {anni.map(a => {
              const isGest = Number(a) === Number(annoGestione);
              const isNext = Number(a) === Number(annoGestione) + 1;
              const isPrev = Number(a) === Number(annoGestione) - 1;
              const isEditable = isGest || isNext || isPrev;
              const v = it.by_anno?.[String(a)];
              const ciclo = v?.ciclo || "";
              const outOfSync = !!v?.out_of_sync;
              return (
                <td key={a} style={{
                  ...td, textAlign: "left", padding: 2, position: "relative",
                  background: isGest ? "#78909c" : (isNext ? "#e1f5fe" : (isPrev ? "#fff3e0" : undefined)),
                  borderLeft: isGest ? "2px solid #fbc02d" : (isNext ? "2px dashed #0288d1" : (isPrev ? "2px dashed #fb8c00" : undefined)),
                  borderRight: isGest ? "2px solid #fbc02d" : (isNext ? "2px dashed #0288d1" : (isPrev ? "2px dashed #fb8c00" : undefined)),
                  outline: outOfSync ? "2px solid #ff9800" : undefined,
                }}>
                  {outOfSync && (
                    <span
                      title={`⚠ Disallineato con Segremat: tu hai impostato manualmente "${v?.ciclo_label || ciclo}" ma in Segremat l'alunno risulta in classe "${v?.settore || ''} ${v?.classe || ''}" (non ancora promosso ufficialmente).`}
                      data-testid={`curr-warn-${rowIdx}-${a}`}
                      style={{
                        position: "absolute", top: 1, right: 2,
                        background: "#ff9800", color: "white",
                        borderRadius: "50%", width: 14, height: 14,
                        fontSize: 9, fontWeight: 900,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "help", zIndex: 2, lineHeight: 1,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                      }}>!</span>
                  )}
                  {isGest && onOpenIscr ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, width: "100%" }}>
                      <IscrizioneBadge
                        counts={iscrCounts?.[Number(it.Cod)]}
                        onClick={() => onOpenIscr(it.Cod)}
                        boxStyle
                        testid={`curr-iscr-badge-${it.Cod}`}
                      />
                      <CicloCheckboxes
                        current={ciclo}
                        editable={isEditable}
                        onSet={(newCiclo) => doSetCiclo(it.Cod, a, newCiclo)}
                        nasDat={it.NasDat || ""}
                        dataTestId={`curr-ciclo-${rowIdx}-${a}`}
                      />
                    </div>
                  ) : (
                    <CicloCheckboxes
                      current={ciclo}
                      editable={isEditable}
                      onSet={(newCiclo) => doSetCiclo(it.Cod, a, newCiclo)}
                      nasDat={it.NasDat || ""}
                      dataTestId={`curr-ciclo-${rowIdx}-${a}`}
                    />
                  )}
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
}


function EmailInline({ cod, value, onUpdate, testid }) {
  const [local, setLocal] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setLocal(value || ""); setDirty(false); }, [value]);

  const isCanossa = local.toLowerCase().endsWith("@canossadiaz.it");
  const hasError = local && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(local);
  const baseColor = !local ? "#aaa" : (isCanossa ? "#1b5e20" : "#c62828");

  const save = async () => {
    if (!dirty) return;
    if (hasError) return;
    setSaving(true); setErr("");
    try {
      await onUpdate(cod, local.trim());
      setDirty(false);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center", width: "100%" }}>
      <input
        data-testid={testid}
        value={local}
        onChange={e => { setLocal(e.target.value); setDirty(true); setErr(""); }}
        onBlur={save}
        onKeyDown={e => {
          if (e.key === "Enter") { e.preventDefault(); save(); e.target.blur(); }
          if (e.key === "Escape") { setLocal(value || ""); setDirty(false); setErr(""); }
        }}
        placeholder="(nessuna email)"
        style={{
          flex: 1,
          padding: "4px 6px",
          fontFamily: "Consolas, monospace",
          fontSize: 11,
          letterSpacing: 0.2,
          color: baseColor,
          border: dirty ? "1.5px solid #ef6c00" : (hasError ? "1.5px solid #c62828" : "1px solid #e0e0e0"),
          background: dirty ? "#fff8e1" : (!local ? "#ffebee" : "white"),
          outline: "none",
          minWidth: 140,
        }}
      />
      {saving && <span style={{ fontSize: 10, color: "#666" }}>…</span>}
      {!saving && !dirty && local && isCanossa && (
        <span title="Email canossadiaz.it confermata" style={{ color: "#1b5e20", fontSize: 11 }}>✓</span>
      )}
      {!saving && !dirty && local && !isCanossa && !hasError && (
        <span title="Email diversa da @canossadiaz.it" style={{ color: "#ef6c00", fontSize: 11 }}>⚠</span>
      )}
      {err && <span title={err} style={{ color: "#c62828", fontSize: 11 }}>!</span>}
    </div>
  );
}



function SetCicloModal({ data, onClose, onSave }) {
  const [val, setVal] = useState(data.ciclo || "");
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "white", padding: 18, borderRadius: 6, minWidth: 380, maxWidth: 460,
        boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
      }}>
        <h3 style={{ margin: 0, color: "#0d47a1", fontSize: 15 }}>
          ✏ Imposta ciclo manuale
        </h3>
        <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
          {data.cog} {data.nom} · A.S. {fmtAnnoLabel(data.anno)}
        </div>
        <div style={{ margin: "14px 0 6px", fontSize: 11, fontWeight: 700, color: "#555", letterSpacing: 0.5 }}>NUOVO CICLO</div>
        <select data-testid="curr-set-ciclo-select" value={val} onChange={e => setVal(e.target.value)}
          style={{ width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid #ccc" }}>
          {CICLO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button data-testid="curr-set-ciclo-cancel" onClick={onClose} style={{ padding: "7px 14px", background: "#eee", border: "1px solid #ccc", fontSize: 12, cursor: "pointer" }}>Annulla</button>
          <button data-testid="curr-set-ciclo-save" onClick={() => onSave(val)} style={{ padding: "7px 18px", background: "#0d47a1", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Salva</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#555", marginBottom: 3, letterSpacing: 0.5 }}>{label}</div>
      {children}
    </div>
  );
}

const th = { padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, borderBottom: "2px solid #283593" };
const td = { padding: "6px 10px", verticalAlign: "middle", textAlign: "left" };
